import { NavLink } from '@tov-plus/frontend-data';
import { useTovQuery } from '@tov-plus/frontend-data';
import { guestApi } from '../app/api';
import { StatePanel } from '../components/StatePanel';
import type { ReviewPassResolution } from '../app/access';
import { guestQueryKeys } from '../lib/data/guestQueryKeys';
import { getGuestErrorMessage } from '../lib/errors/guestErrors';
import { formatGuestDateTime } from '../lib/localization/guestLocale';
import { msg, TovCard } from '@tov-plus/ui';
function getSectionSummary(contentPayload: Record<string, unknown>) {
  const candidate = [
    contentPayload.summary,
    contentPayload.description,
    contentPayload.subtitle,
    contentPayload.body,
  ].find(
    (value): value is string =>
      typeof value === 'string' && value.trim().length > 0,
  );
  return candidate ?? 'Published guest-facing content is available for review.';
}

const reviewerPreviewErrorMessage = msg(
  'pages.reviewer_preview_page.reviewer_preview_could_not_be_loaded',
);

export function ReviewerPreviewPage(props: {
  occasionSlug: string;
  reviewPass: ReviewPassResolution;
  localeCode?: string;
  timezone?: string | null;
}) {
  const previewQuery = useTovQuery({
    queryKey: guestQueryKeys.reviewerPreview(
      props.occasionSlug,
      props.reviewPass.token,
    ),
    queryFn: () =>
      guestApi.getReviewerPreview(props.occasionSlug, {
        reviewToken: props.reviewPass.token,
        previewMode: props.reviewPass.previewMode,
      }),
  });
  const preview = previewQuery.data ?? null;
  if (previewQuery.isLoading) {
    return (
      <div className="guest-standalone">
        <StatePanel preset="loading" />
      </div>
    );
  }
  if (previewQuery.isError || !preview) {
    return (
      <div className="guest-standalone">
        <StatePanel
          preset="unknown-error"
          detail={getGuestErrorMessage(
            previewQuery.error,
            reviewerPreviewErrorMessage,
          )}
          actions={
            <NavLink className="primary-button" to="/access">
              {msg(
                'pages.reviewer_preview_page.pages_reviewer_preview_page_review_access_details',
              )}{' '}
            </NavLink>
          }
        />
      </div>
    );
  }
  return (
    <div className="guest-standalone">
      <section className="page-grid">
        <TovCard variant="content" as="div" isWide className="hero-card">
          <p className="page-eyebrow">
            {msg(
              'pages.reviewer_preview_page.pages_reviewer_preview_page_reviewer_preview',
            )}
          </p>
          <h2>
            {preview.websiteIdentity.siteTitleOverride ??
              preview.occasionDisplayName}
          </h2>
          <p>
            {msg(
              'pages.reviewer_preview_page.pages_reviewer_preview_page_this_is_a_guest_safe_reviewer_rendering_for',
            )}{' '}
          </p>
          <div className="hero-card__stats">
            <article>
              <strong>{preview.sections.length}</strong>
              <span>
                {msg(
                  'pages.reviewer_preview_page.pages_reviewer_preview_page_visible_section',
                )}
              </span>
            </article>
            <article>
              <strong>{preview.navItems.length}</strong>
              <span>
                {msg(
                  'pages.reviewer_preview_page.pages_reviewer_preview_page_navigation_item',
                )}
              </span>
            </article>
            <article>
              <strong>{preview.previewMode.replaceAll('_', ' ')}</strong>
              <span>
                {msg(
                  'pages.reviewer_preview_page.pages_reviewer_preview_page_preview_mode',
                )}
              </span>
            </article>
            <article>
              <strong>
                {formatGuestDateTime(
                  preview.expiresAt,
                  props.localeCode,
                  props.timezone,
                )}
              </strong>
              <span>
                {msg(
                  'pages.reviewer_preview_page.pages_reviewer_preview_page_pass_expiry',
                )}
              </span>
            </article>
          </div>
          {preview.websiteIdentity.subtitleTagline ? (
            <p>{preview.websiteIdentity.subtitleTagline}</p>
          ) : null}
          <div className="action-row">
            <NavLink className="secondary-link" to="/access">
              {msg(
                'pages.reviewer_preview_page.pages_reviewer_preview_page_review_access_details',
              )}{' '}
            </NavLink>
          </div>
        </TovCard>

        <TovCard variant="content" isWide>
          <p className="page-eyebrow">
            {msg(
              'pages.reviewer_preview_page.pages_reviewer_preview_page_published_navigation',
            )}
          </p>
          <div className="detail-list">
            {preview.navItems.length > 0 ? (
              preview.navItems.map((item) => (
                <article key={item.id}>
                  <strong>{item.label}</strong>
                  <span>
                    {item.targetPath
                      ? `Route target: ${item.targetPath}`
                      : item.targetSectionId
                        ? `Section target: ${item.targetSectionId}`
                        : msg(
                            'pages.reviewer_preview_page.pages_reviewer_preview_page_no_target_configured',
                          )}
                  </span>
                </article>
              ))
            ) : (
              <article>
                <strong>
                  {msg(
                    'pages.reviewer_preview_page.pages_reviewer_preview_page_no_navigation_configured',
                  )}
                </strong>
                <span>
                  {msg(
                    'pages.reviewer_preview_page.pages_reviewer_preview_page_the_reviewer_preview_is_rendering_section_content_only',
                  )}{' '}
                </span>
              </article>
            )}
          </div>
        </TovCard>

        <TovCard variant="content" isWide>
          <p className="page-eyebrow">
            {msg(
              'pages.reviewer_preview_page.pages_reviewer_preview_page_visible_guest_sections',
            )}
          </p>
          <div className="detail-list">
            {preview.sections.map((section) => (
              <article key={section.id}>
                <strong>
                  {section.anchorLabel ??
                    (typeof section.contentPayload.title === 'string'
                      ? section.contentPayload.title
                      : section.sectionKey)}
                </strong>
                <span>{getSectionSummary(section.contentPayload)}</span>
                <span>{section.type.replaceAll('_', ' ')}</span>
              </article>
            ))}
          </div>
        </TovCard>
      </section>
    </div>
  );
}
